/**
 * L-Shape with Angled Corners ("Cut-Corner L") + Breakfast-Nook SET Configurator
 * uscushion.com · 2026-10
 *
 * Runs INSIDE the shared cushion-configurator-block.liquid (formula_type == 'lcut').
 *
 * TWO shapes, switchable via the shape grid (state.shape):
 *   'lcut'  — a 7-sided L with two angled (beveled) corners (single piece, original).
 *   'nook'  — a 3-piece corner-bench / breakfast-nook SET for a RIGHT-ANGLE (90°)
 *             corner. "Measure the bench, we compute the cushions" (zotra.de model):
 *             the customer gives only LEFT length + RIGHT length + SEAT DEPTH, picks a
 *             corner cushion (square or diamond), and we DERIVE the 3 pieces:
 *               corner  = depth × depth (square; diamond = same + chamfered front)
 *               left seat  = (left  − depth) × depth
 *               right seat = (right − depth) × depth
 *             One add-to-cart = the whole set, price = sum of the 3 pieces, one
 *             shared thickness. Beveled (non-90°) corners are a v2 follow-up.
 *
 * ── lcut inputs (inches) — 7 edges define the outline, left bevel DERIVED:
 *     a=long-arm top, b=upright inner H, c=upright top W, d=upright right H,
 *     e1/e2=right bevel legs, f=long-arm bottom, T=thickness. Orientation right/left.
 * ── nook inputs (inches): sideL, sideR, depth, T + cornerShape (square|diamond).
 *     The 3 cushions are DERIVED, never measured piece-by-piece.
 *
 * Pricing口径: seats/square corner = bench formula per rectangle (chaise/lshape口径);
 * diamond corner = material on bbox + shipping on true polygon area (diamond engine口径).
 * No Shopify-side change — this shape lives inside the lcut product (formula_type=lcut).
 */
(function () {
  'use strict';

  /* ─── Cost constants (same as bench / polygon / chaise / diamond) ─── */
  var FABRIC_PRICE_RMB  = 25;
  var FABRIC_RATE_RMB   = { marine: 54 };
  function fabricRate() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return FABRIC_RATE_RMB[g] || FABRIC_PRICE_RMB;
  }
  var FOAM_PRICE_RMB    = 1750;
  var FOAM_DENSITY      = 35;
  var FABRIC_GSM        = 400;
  var SHIPPING_RMB      = 90;
  var ACCESSORIES_RMB   = 30;
  var EXCHANGE_RATE     = 6.8;
  var PAYMENT_FEE_RATE  = 0.049;
  var TARGET_COST_RATIO = 0.40;
  var MARINE_COST_RATIO = 0.38;
  var COVER_COST_RATIO  = 0.55;   /* Extra covers = 45% margin (AOV add-on) */
  function targetRatio() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return g === 'marine' ? MARINE_COST_RATIO : TARGET_COST_RATIO;
  }
  var MIN_PRICE         = 45;

  /* ─── Addon pricing (USD) — mirrors the shared block's controls ─── */
  var ADDON = {
    piping:      { none: 0, single: 5, double: 8 },
    pipingColor: { match: 0, white: 5, black: 5, navy: 5, cream: 5 },
    straps:      { none: 0, two: 2, four: 4 },
    zipper:      { regular: 0, invisible: 0 },
    antislip:    { none: 0, black_pad: 5, beige_pad: 5, velcro: 9 },
    throwPillow: {
      none: 0,
      '2pcs_12x20': 29, '2pcs_16x16': 29, '2pcs_18x18': 32,
      '2pcs_20x20': 35, '2pcs_24x24': 38, '2pcs_custom': 40,
      '4pcs_12x20': 51, '4pcs_16x16': 51, '4pcs_18x18': 57,
      '4pcs_20x20': 63, '4pcs_24x24': 67, '4pcs_custom': 70
    },
    pillowCovers: {
      none: 0,
      '2pcs_12x20': 19, '2pcs_16x16': 19, '2pcs_18x18': 21,
      '2pcs_20x20': 24, '2pcs_24x24': 29, '2pcs_custom': 31,
      '4pcs_12x20': 32, '4pcs_16x16': 32, '4pcs_18x18': 36,
      '4pcs_20x20': 42, '4pcs_24x24': 52, '4pcs_custom': 55
    },
    armrestPillow: { none: 0, square_2pcs: 30, round_2pcs: 30 }
  };
  /* ─── Units (depolster.de): inputs in cm, pricing engine unchanged (inch). inch→cm only for display. ─── */
  var CM_PER_IN = 2.54;
  function cmS(inch) { return round1(inch * CM_PER_IN); }
  function parseNum(v) { return parseFloat(String(v == null ? '' : v).replace(',', '.')) || 0; }
  function round1(n) { return Math.round(n * 10) / 10; }
  var PILLOW_SIZE = { '12x20': '30×50 cm', '16x16': '40×40 cm', '18x18': '45×45 cm', '20x20': '50×50 cm', '24x24': '60×60 cm', 'custom': 'Sondermaß' };
  function pillowLabel(v) { var m = /^(\d)pcs_(.+)$/.exec(v); return m ? (m[1] + ' Stück ' + (PILLOW_SIZE[m[2]] || m[2])) : v; }
  var ARMREST_LABEL = { square_2pcs: 'Eckig — 2 Stück', round_2pcs: 'Rund — 2 Stück' };


  var LABELS = {
    none: 'Keine', single: 'Einfacher Keder', double: 'Doppelter Keder',
    match: 'Passend zum Stoff', white: 'Weißer Keder', black: 'Schwarzer Keder', navy: 'Marineblauer Keder', cream: 'Cremefarbener Keder',
    two: '2 Bänder', four: '4 Bänder',
    regular: 'Standard-Reißverschluss', invisible: 'Nahtverdeckter Reißverschluss',
    velcro: 'Klettband', black_pad: 'Schwarze Unterlage', beige_pad: 'Beige Unterlage',
    right: 'Aufrecht rechts', left: 'Aufrecht links'
  };

  var FABRIC_COLORS = {
    'blue':'#2c3e6b','navy':'#1a2540','teal':'#2a7a6b','green':'#4a7a2a',
    'sage':'#a8b89a','red':'#b83232','coral':'#d85a45','orange':'#d4783a',
    'yellow':'#d4a83a','gold':'#c8963a','pink':'#d4649a','purple':'#6a4a9a',
    'lavender':'#9a8ab8','brown':'#7a5a3a','camel':'#c8a878','tan':'#c8b08a',
    'beige':'#d4c8a8','cream':'#ede8df','ivory':'#f0ead8','white':'#f5f5f0',
    'gray':'#9a9a9a','grey':'#9a9a9a','charcoal':'#4a4a4a','black':'#1a1a1a',
    'mint':'#98d4b4','rust':'#b7502a','mustard':'#c8a020','olive':'#6b702a',
    'blush':'#de9898','mauve':'#b07898','burgundy':'#6b1a2a','taupe':'#8a7a6a',
    'sand':'#c8b898','silver':'#bfc0c2','steel blue':'#4a7a98',
    'dark olive':'#4a4a2a','dark green':'#2a5a2a','dark brown':'#4a3020',
    'dark blue':'#1a2a5a','dark teal':'#1a5a5a','dark gray':'#5a5a5a',
    'light gray':'#c8c8c8','gray blue':'#6a7a8a','yellow green':'#8ab82a',
    'gray black':'#3a3a3a'
  };

  /* ─── Shape-card render images (Shopify Files) ─── */
  var NOOK_CDN = 'https://cdn.shopify.com/s/files/1/0676/5687/6232/files/';
  var NOOK_IMG = {
    lcut:     NOOK_CDN + 'L-Shape_Cushion_with_Angled_Corners.png?v=1792134651',
    square:   NOOK_CDN + 'L_shape_cushion_3_pieces.png?v=1792393704',
    diamond:  NOOK_CDN + 'nook-cushion-set-diamond-corner.webp?v=1792132166',
    triangle: NOOK_CDN + 'nook-cushion-set-triangle-corner.webp?v=1792132166'
  };

  /* ─── State ─── */
  var state = {
    shape: 'lcut',
    /* lcut */
    orient: 'right',
    a: 0, b: 0, c: 0, d: 0, e1: 0, e2: 0, f: 0,
    /* nook corner type: 'square' | 'triangle' | 'diamond' */
    cornerShape: 'square',
    /* square / triangle: "measure the bench" — left length, right length, seat depth → derive 3 pieces */
    sideL: 0, sideR: 0, depth: 0,
    /* diamond: explicit = real diamond corner (te/mw/be/th/bh) + two explicit benches (L×W) */
    dte: 0, dmw: 0, dbe: 0, dth: 0, dbh: 0,   /* diamond hexagon: top edge, max width, bottom edge, top height, bottom height */
    bl1: 0, bw1: 0, bl2: 0, bw2: 0,           /* left bench L×W, right bench L×W */
    /* shared */
    thickness: 0,
    fabric: null, piping: '', pipingColor: 'match', straps: '',
    zipper: 'regular', zipperPlacement: '', antislip: '',
    extraCovers: '0', throwPillow: 'none', pillowCovers: 'none',
    armrestPillow: 'none', notes: ''
  };

  var els = {};

  /* ─── Color helpers ─── */
  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    return [parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16)];
  }
  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(function (c) {
      var x = Math.round(Math.max(0, Math.min(255, c))).toString(16);
      return x.length === 1 ? '0' + x : x;
    }).join('');
  }
  function lighten(hex, r) { var c = hexToRgb(hex); return rgbToHex(c[0] + (255 - c[0]) * r, c[1] + (255 - c[1]) * r, c[2] + (255 - c[2]) * r); }
  function darken(hex, r)  { var c = hexToRgb(hex); return rgbToHex(c[0] * (1 - r), c[1] * (1 - r), c[2] * (1 - r)); }
  function extractTag(tags, prefix) {
    if (!tags) return '';
    for (var i = 0; i < tags.length; i++) {
      var t = (typeof tags[i] === 'string' ? tags[i] : '').toLowerCase().trim();
      if (t.indexOf(prefix + ':') === 0) return tags[i].split(':')[1].trim().toLowerCase();
    }
    return '';
  }

  /* Resolve the top / stroke fabric colors for the preview */
  function fabricPaint(muted) {
    var topC = '#EDE9E0', frontC = '#D5D0C2', strokeC = '#2A2A28', lum = 200;
    if (!muted && state.fabric && state.fabric.tags) {
      var colorTag = extractTag(state.fabric.tags, 'color');
      var baseHex = FABRIC_COLORS[colorTag];
      if (baseHex) {
        topC = lighten(baseHex, 0.50); frontC = lighten(baseHex, 0.20); strokeC = darken(baseHex, 0.45);
        var sc0 = hexToRgb(strokeC);
        if ((sc0[0] + sc0[1] + sc0[2]) / 3 > 120) strokeC = '#2A2A28';
        var fc = hexToRgb(baseHex); lum = (fc[0] + fc[1] + fc[2]) / 3;
      }
    }
    if (muted) { topC = '#F0EDE6'; frontC = '#E6E2D8'; strokeC = '#B8B2A6'; }
    return { topC: topC, frontC: frontC, strokeC: strokeC, lum: lum };
  }

  /* ═══════════════════════════════════════════════════════════
     lcut Geometry — 7-vertex L with two bevels, from the 7 edges.
     ═══════════════════════════════════════════════════════════ */
  function geom(d) {
    var a = d.a, b = d.b, c = d.c, dd = d.d, e1 = d.e1, e2 = d.e2, f = d.f;
    var g1 = a + c - e1 - f;    // left bevel horizontal leg
    var g2 = (dd + e2) - b;     // left bevel vertical leg == long-arm depth
    var longArmW = a + c;       // overall width
    var longArmD = g2;          // long-arm front-to-back depth
    var uprightW = c;
    var uprightH = b;           // upright height above the long arm

    var errs = [];
    if (!(a > 0 && b > 0 && c > 0 && dd > 0 && e1 > 0 && e2 > 0 && f > 0)) errs.push('alle Maße');
    if (longArmD <= 0) errs.push('Höhe rechts (D+E2) muss größer als die Innenhöhe (B) sein, damit der lange Schenkel Tiefe hat');
    if (g1 < -0.05) errs.push('Schräge links: Oberkante (A) + obere Breite (C) muss ≥ Schräge quer (E1) + Unterkante (F) sein');
    if (e1 >= longArmW) errs.push('Schräge rechts quer (E1) ist zu groß');

    var xC = a;
    var raw = [
      [xC - a, b],            // A  long-arm top-left (top of left bevel)
      [xC, b],                // B  inner corner (concave)
      [xC, 0],                // C  upright top-left
      [xC + c, 0],            // D  upright top-right
      [xC + c, dd],           // E  upright right-edge bottom (top of right bevel)
      [xC + c - e1, dd + e2], // G  bottom of right bevel
      [xC + c - e1 - f, dd + e2] // H bottom-left (bottom of left bevel)
    ];
    if (state.orient === 'left') { for (var i = 0; i < raw.length; i++) raw[i][0] = -raw[i][0]; }

    var minX = 1e9, minY = 1e9, maxX = -1e9, maxY = -1e9;
    for (var j = 0; j < raw.length; j++) {
      minX = Math.min(minX, raw[j][0]); maxX = Math.max(maxX, raw[j][0]);
      minY = Math.min(minY, raw[j][1]); maxY = Math.max(maxY, raw[j][1]);
    }
    var ccx = (minX + maxX) / 2, ccy = (minY + maxY) / 2;
    var verts = raw.map(function (p) { return [p[0] - ccx, p[1] - ccy]; });

    return {
      verts: verts, g1: g1, g2: g2,
      longArmW: longArmW, longArmD: longArmD, uprightW: uprightW, uprightH: uprightH,
      overallW: maxX - minX, overallH: maxY - minY, valid: errs.length === 0, errs: errs
    };
  }

  function bounds(verts) {
    var minX = 1e9, minY = 1e9, maxX = -1e9, maxY = -1e9, i;
    for (i = 0; i < verts.length; i++) {
      minX = Math.min(minX, verts[i][0]); maxX = Math.max(maxX, verts[i][0]);
      minY = Math.min(minY, verts[i][1]); maxY = Math.max(maxY, verts[i][1]);
    }
    return { w: maxX - minX, h: maxY - minY, mx: Math.max(Math.abs(minX), Math.abs(maxX)), my: Math.max(Math.abs(minY), Math.abs(maxY)) };
  }

  /* ═══ Diamond (hexagon) geometry + pricing — ported from diamond engine ═══ */
  function polyAreaIn2(v) { var a = 0; for (var i = 0; i < v.length; i++) { var j = (i+1)%v.length; a += v[i][0]*v[j][1] - v[j][0]*v[i][1]; } return Math.abs(a)/2; }
  function perimIn(v)    { var p = 0; for (var i = 0; i < v.length; i++) { var j = (i+1)%v.length; p += Math.hypot(v[j][0]-v[i][0], v[j][1]-v[i][1]); } return p; }
  function bboxAreaIn2(v){ var xs = v.map(function(p){return p[0];}), ys = v.map(function(p){return p[1];}); return (Math.max.apply(null,xs)-Math.min.apply(null,xs))*(Math.max.apply(null,ys)-Math.min.apply(null,ys)); }
  function diamondBase(v, T) {
    var IN = 39.37;
    var matA = bboxAreaIn2(v)/(IN*IN), perM = perimIn(v)/IN, Tm = T/IN;
    var fabA = matA*2 + perM*Tm, foamV = matA*Tm;
    var wt = foamV*FOAM_DENSITY + fabA*FABRIC_GSM/1000;
    var rmb = fabA*fabricRate() + foamV*FOAM_PRICE_RMB + wt*SHIPPING_RMB + ACCESSORIES_RMB;
    return rmb/EXCHANGE_RATE/(1-PAYMENT_FEE_RATE)/targetRatio();
  }
  function diamondCover(v, T) {
    var IN = 39.37;
    var actA = polyAreaIn2(v)/(IN*IN), matA = bboxAreaIn2(v)/(IN*IN), perM = perimIn(v)/IN, Tm = T/IN;
    var matF = matA*2 + perM*Tm;
    var shipW = (actA*2 + perM*Tm)*FABRIC_GSM/1000;
    var rmb = matF*fabricRate() + shipW*SHIPPING_RMB + ACCESSORIES_RMB;
    return rmb/EXCHANGE_RATE/(1-PAYMENT_FEE_RATE)/COVER_COST_RATIO;
  }

  /* ─── Mini flat-view SVGs for the shape / orientation cards ─── */
  function miniLcut(orient) {
    var o = state.orient; state.orient = orient;
    var g = geom({ a: 93, b: 31, c: 19, d: 30, e1: 22.5, e2: 19, f: 72.5 });
    state.orient = o;
    var b = bounds(g.verts), i, pts = [];
    var sc = Math.min(52 / b.w, 36 / b.h);
    for (i = 0; i < g.verts.length; i++) pts.push((30 + g.verts[i][0] * sc).toFixed(1) + ',' + (22 + g.verts[i][1] * sc).toFixed(1));
    return '<svg viewBox="0 0 60 44" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">'
      + '<polygon points="' + pts.join(' ') + '" fill="#EDE9E0" stroke="#2A2A28" stroke-width="1.6" stroke-linejoin="round"/></svg>';
  }
  function miniNook(kind) {
    /* L layout icon: corner top-left, long seat horizontal top-right, short seat vertical bottom-left */
    var corner = (kind === 'triangle')
      ? '<polygon points="17,6 17,22 3,22" fill="#EDE9E0" stroke="#2A2A28" stroke-width="1.2" stroke-linejoin="round"/>'
      : '<polygon points="5,6 15,6 19,11 12,24 8,24 1,11" fill="#EDE9E0" stroke="#2A2A28" stroke-width="1.2" stroke-linejoin="round"/>';
    return '<svg viewBox="0 0 60 44" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">'
      + corner
      + '<rect x="23" y="6" width="34" height="9" rx="1" fill="#EDE9E0" stroke="#2A2A28" stroke-width="1.2"/>'
      + '<rect x="3" y="27" width="9" height="15" rx="1" fill="#EDE9E0" stroke="#2A2A28" stroke-width="1.2"/></svg>';
  }
  /* Corner-shape selector icons: a plain square vs a square with a chamfered front corner */
  function miniCorner(kind) {
    var sq  = '<rect x="11" y="8" width="24" height="24" rx="1.5" fill="#EDE9E0" stroke="#2A2A28" stroke-width="1.6"/>';
    var tri = '<polygon points="11,8 35,8 11,32" fill="#EDE9E0" stroke="#2A2A28" stroke-width="1.6" stroke-linejoin="round"/>';
    var dia = '<polygon points="16,8 30,8 35,20 27,32 19,32 11,20" fill="#EDE9E0" stroke="#2A2A28" stroke-width="1.6" stroke-linejoin="round"/>';
    var g = kind === 'diamond' ? dia : kind === 'triangle' ? tri : sq;
    return '<svg viewBox="0 0 46 40" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">' + g + '</svg>';
  }

  /* ─── Pricing — bench rectangle (same engine as chaise) ─── */
  function calcPiecePrice(L, W, T) {
    if (!L || !W || !T) return 0;
    var Lm = L / 39.37, Wm = W / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var foamVolume = Lm * Wm * Tm;
    var totalWeight = foamVolume * FOAM_DENSITY + fabricArea * FABRIC_GSM / 1000;
    var totalRMB = fabricArea * fabricRate() + foamVolume * FOAM_PRICE_RMB + totalWeight * SHIPPING_RMB + ACCESSORIES_RMB;
    return totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / targetRatio();
  }

  function isNook() { return state.shape === 'nook'; }
  function cornerIsDiamond()  { return state.cornerShape === 'diamond'; }
  function cornerIsTriangle() { return state.cornerShape === 'triangle'; }
  /* diamond mode = a wholly different input model: real diamond corner + 2 explicit benches */
  function diamondMode() { return isNook() && cornerIsDiamond(); }

  /* ── square / triangle ("measure the bench, derive the 3 pieces") ── */
  function derivedSeatL() { return +(state.sideL - state.depth).toFixed(2); }   /* left seat length  */
  function derivedSeatR() { return +(state.sideR - state.depth).toFixed(2); }   /* right seat length */
  function seatsValid()   { return derivedSeatL() >= 4 && derivedSeatR() >= 4; }
  function cornerValid()  { return state.depth >= 8 && state.sideL >= state.depth + 4 && state.sideR >= state.depth + 4; }
  function bareNookValid(){ return state.sideL > 0 && state.sideR > 0 && state.depth > 0 && cornerValid() && seatsValid(); }

  /* square/triangle corner footprint, local coords: inner/back corner at (0,0), walls along +x/+y. */
  function squareCornerVerts()   { var d = state.depth; return [[0, 0], [d, 0], [d, d], [0, d]]; }
  function triangleCornerVerts() { var d = state.depth; return [[0, 0], [d, 0], [0, d]]; }   /* right angle at back corner, hypotenuse to the room */
  function cornerVerts() { return cornerIsTriangle() ? triangleCornerVerts() : squareCornerVerts(); }
  function cornerBasePrice() { return calcPiecePrice(state.depth, state.depth, state.thickness); }   /* square & triangle: d×d bbox */
  function cornerCoverPrice() { return coverPiece(state.depth, state.depth, state.thickness); }

  /* Diamond: AB=right depth, FE=left depth, ED/BC manually measured,
     CD manually measured. AF is a derived 45-degree bevel. */
  function diamondBevel(te, be, bc, leftDepth, cd) {
    var x=te-be, y=leftDepth-bc;
    var disc=2*cd*cd-(x-y)*(x-y);
    if(disc<0)return NaN;
    return (-(x+y)+Math.sqrt(disc))/2;
  }
  function diamondModel(te,be,bc,leftDepth,cd) {
    var k=diamondBevel(te,be,bc,leftDepth,cd);
    return [[k,0],[k+te,0],[k+te,bc],[be,k+leftDepth],[0,k+leftDepth],[0,k]];
  }
  function diamondHexVerts() {
    return diamondModel(state.bw2,state.dbe,state.dbh,state.bw1,state.dcd);
  }
  function diamondCornerValid() {
    var k=diamondBevel(state.bw2,state.dbe,state.dbh,state.bw1,state.dcd);
    return state.bw2>=4 && state.bw1>=2 && state.dbe>=2 && state.dbh>=2 &&
      state.dcd>=2 && Number.isFinite(k) && k>0.1 &&
      state.dbe<k+state.bw2 && state.dbh<k+state.bw1;
  }
  function diamondBenchesValid() {
    return state.bl1>=6 && state.bw1>=2 && state.bl2>=6 && state.bw2>=2;
  }
  function diamondDimsValid() { return diamondCornerValid() && diamondBenchesValid(); }

  function nookDimsValid() { return diamondMode() ? diamondDimsValid() : bareNookValid(); }

  function calcPrice() {
    if (isNook()) {
      if (!(nookDimsValid() && state.thickness)) return null;
      var T = state.thickness, p;
      if (diamondMode()) {
        p = diamondBase(diamondHexVerts(), T)
          + calcPiecePrice(state.bl1, state.bw1, T)
          + calcPiecePrice(state.bl2, state.bw2, T);
      } else {
        p = cornerBasePrice()
          + calcPiecePrice(derivedSeatL(), state.depth, T)
          + calcPiecePrice(derivedSeatR(), state.depth, T);
      }
      return Math.max(p * (window.__CC_COVER_FACTOR || 1), MIN_PRICE);
    }
    var g = geom(state);
    if (!g.valid || !state.thickness) return null;
    var pl = calcPiecePrice(g.longArmW, g.longArmD, state.thickness)
           + calcPiecePrice(g.uprightW, g.uprightH, state.thickness);
    return Math.max(pl * (window.__CC_COVER_FACTOR || 1), MIN_PRICE);
  }

  /* One extra cover = fabric-only shells of all pieces (no foam). */
  function coverPiece(L, W, T) {
    if (!L || !W || !T) return 0;
    var Lm = L / 39.37, Wm = W / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var weight = fabricArea * FABRIC_GSM / 1000;
    var totalRMB = fabricArea * fabricRate() + weight * SHIPPING_RMB + ACCESSORIES_RMB;
    return totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / COVER_COST_RATIO;
  }
  function extraCoverUnit() {
    if (isNook()) {
      if (!(nookDimsValid() && state.thickness)) return 0;
      var T = state.thickness;
      if (diamondMode()) {
        return Math.round(diamondCover(diamondHexVerts(), T)
          + coverPiece(state.bl1, state.bw1, T)
          + coverPiece(state.bl2, state.bw2, T));
      }
      return Math.round(cornerCoverPrice()
        + coverPiece(derivedSeatL(), state.depth, T)
        + coverPiece(derivedSeatR(), state.depth, T));
    }
    var g = geom(state);
    if (!g.valid || !state.thickness) return 0;
    return Math.round(coverPiece(g.longArmW, g.longArmD, state.thickness)
                    + coverPiece(g.uprightW, g.uprightH, state.thickness));
  }

  function updateExtraCoverLabels() {
    if (!els.extraCovers) return;
    var unit = extraCoverUnit();
    var opts = els.extraCovers.options;
    for (var i = 0; i < opts.length; i++) {
      var n = parseInt(opts[i].value) || 0;
      if (n === 0) { opts[i].text = 'Kein zusätzlicher Bezug'; continue; }
      var label = (n === 1 ? '1 Set' : n + ' Sets');
      opts[i].text = unit > 0 ? label + ' (+' + (unit * n) + ' €)' : label;
    }
  }

  function getAddonTotal() {
    return (ADDON.piping[state.piping] || 0)
      + (ADDON.pipingColor[state.pipingColor] || 0)
      + (ADDON.straps[state.straps] || 0) + (ADDON.zipper[state.zipper] || 0)
      + (ADDON.antislip[state.antislip] || 0) + extraCoverUnit() * (parseInt(state.extraCovers) || 0)
      + (ADDON.throwPillow[state.throwPillow] || 0)
      + (ADDON.pillowCovers[state.pillowCovers] || 0)
      + (ADDON.armrestPillow[state.armrestPillow] || 0);
  }

  function hasDims() {
    return isNook()
      ? (nookDimsValid() && state.thickness >= 1)
      : (geom(state).valid && state.thickness >= 1);
  }

  function isComplete() {
    return hasDims() && state.fabric && state.piping && state.straps && state.antislip;
  }

  /* ═══════════════════════════════════════════════════════════
     SVG Preview — dispatch per shape
     ═══════════════════════════════════════════════════════════ */
  function drawCushion() {
    var svg = els.dimSvg;
    if (!svg) return;
    if (isNook()) { drawNook(); return; }
    svg.setAttribute('viewBox', '0 0 440 270');

    var T = state.thickness;
    var LDEF = { a: 93, b: 15, c: 19, d: 30, e1: 22.5, e2: 19, f: 72.5 };
    var empty = !(state.a && state.b && state.c && state.d && state.e1 && state.e2 && state.f);
    var mixed = { orient: state.orient };
    ['a', 'b', 'c', 'd', 'e1', 'e2', 'f'].forEach(function (k) { mixed[k] = state[k] || LDEF[k]; });
    var g = geom(mixed);
    if (!g.valid && empty) { LDEF.orient = state.orient; g = geom(LDEF); mixed = LDEF; }   // half-typed values can't form a shape yet: keep the sample
    var TODO = '#C0612B', DIMC = '#1a4f78', mir = (state.orient === 'left') ? -1 : 1;

    function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }
    function pt(a) { return a[0].toFixed(1) + ',' + a[1].toFixed(1); }
    function f(n)  { return n.toFixed(1); }

    var paint = fabricPaint(false);
    var topC = paint.topC, frontC = paint.frontC, strokeC = paint.strokeC, fabricLum = paint.lum;

    var h = '';
    h += '<defs><marker id="lc-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">'
      + '<path d="M1 1.5L8.5 5L1 8.5" fill="none" stroke="context-stroke" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></marker></defs>';

    var verts = g.verts, b = bounds(verts), i, j;
    var cx = 220, cy = 118;
    var sc = clamp(Math.min(300 / b.w, 160 / b.h), 1.4, 9);
    var halfHpx = b.my * sc;
    var thickPx = clamp((T || 3) * 6, 8, 40);

    var topPts = [], botPts = [];
    for (i = 0; i < verts.length; i++) {
      var x = cx + verts[i][0] * sc, y = cy + verts[i][1] * sc;
      topPts.push([x, y]); botPts.push([x, y + thickPx]);
    }

    function inPoly(p) {
      var inside = false, n = topPts.length;
      for (var a = 0, c = n - 1; a < n; c = a++) {
        var xi = topPts[a][0], yi = topPts[a][1], xj = topPts[c][0], yj = topPts[c][1];
        if (((yi > p[1]) !== (yj > p[1])) && (p[0] < (xj - xi) * (p[1] - yi) / (yj - yi) + xi)) inside = !inside;
      }
      return inside;
    }

    var frontEdges = [], backEdges = [];
    for (i = 0; i < topPts.length; i++) {
      j = (i + 1) % topPts.length;
      var ex = topPts[j][0] - topPts[i][0], ey = topPts[j][1] - topPts[i][1];
      var el = Math.hypot(ex, ey) || 1, nx = ey / el, ny = -ex / el;
      var mx0 = (topPts[i][0] + topPts[j][0]) / 2, my0 = (topPts[i][1] + topPts[j][1]) / 2;
      var outGt = !inPoly([mx0 + nx * 3, my0 + ny * 3]);
      var oy = outGt ? ny : -ny;
      if (oy > 0.12) frontEdges.push(i);
      else if (oy < -0.12) backEdges.push(i);
    }
    function longest(list) {
      var best = list.length ? list[0] : 0, bl = -1;
      list.forEach(function (ei) {
        var k = (ei + 1) % topPts.length;
        var L = Math.hypot(topPts[k][0] - topPts[ei][0], topPts[k][1] - topPts[ei][1]);
        if (L > bl) { bl = L; best = ei; }
      });
      return best;
    }

    if ((state.straps === 'two' || state.straps === 'four') && frontEdges.length) {
      var tieC = fabricLum < 100 ? '#8A8A85' : '#4A4A47', hangLen = 15;
      function drawTie(bx, by, dir) {
        var spread = 4;
        h += '<path d="M' + f(bx - spread * 0.5) + ' ' + f(by) + ' Q' + f(bx - spread + dir * 3) + ' ' + f(by + hangLen * 0.5) + ' ' + f(bx - spread * 1.5 + dir * 5) + ' ' + f(by + hangLen) + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
        h += '<path d="M' + f(bx + spread * 0.5) + ' ' + f(by) + ' Q' + f(bx + spread + dir * 2) + ' ' + f(by + hangLen * 0.5) + ' ' + f(bx + spread * 1.5 + dir * 4) + ' ' + f(by + hangLen) + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
      }
      var le = longest(frontEdges), lk = (le + 1) % topPts.length;
      var mcx = (botPts[le][0] + botPts[lk][0]) / 2, mcy = (botPts[le][1] + botPts[lk][1]) / 2;
      drawTie(mcx, mcy, 0);
      if (state.straps === 'four') {
        drawTie(botPts[le][0] * 0.65 + mcx * 0.35, botPts[le][1] * 0.65 + mcy * 0.35, -1);
        drawTie(botPts[lk][0] * 0.65 + mcx * 0.35, botPts[lk][1] * 0.65 + mcy * 0.35, 1);
      }
    }

    frontEdges.forEach(function (ei) {
      var k = (ei + 1) % topPts.length;
      h += '<path d="M ' + pt(topPts[ei]) + ' L ' + pt(topPts[k]) + ' L ' + pt(botPts[k]) + ' L ' + pt(botPts[ei]) + ' Z" fill="' + frontC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    });

    var topPath = 'M ' + pt(topPts[0]);
    for (i = 1; i < topPts.length; i++) topPath += ' L ' + pt(topPts[i]);
    topPath += ' Z';
    h += '<path d="' + topPath + '" fill="' + topC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    if (backEdges.length) {
      backEdges.forEach(function (ei) {
        var k = (ei + 1) % topPts.length;
        h += '<path d="M ' + pt(topPts[ei]) + ' L ' + pt(topPts[k]) + '" fill="none" stroke="#3A7D2C" stroke-width="3" stroke-linecap="round" opacity="0.92"/>';
      });
      var ze = longest(backEdges), zk = (ze + 1) % topPts.length;
      var zt = [(topPts[ze][0] + topPts[zk][0]) / 2, (topPts[ze][1] + topPts[zk][1]) / 2];
      h += '<text x="' + f(zt[0]) + '" y="' + f(zt[1] + 14) + '" text-anchor="middle" font-size="11" font-family="Poppins,sans-serif" font-weight="600" fill="#3A7D2C">Reißverschluss</text>';
    }

    if (state.piping === 'single' || state.piping === 'double') {
      var pipC = '#F5F1E8', pw = 2.2;
      h += '<path d="' + topPath + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linejoin="round" opacity="0.85"/>';
      if (state.piping === 'double') {
        frontEdges.forEach(function (ei) {
          var k = (ei + 1) % topPts.length;
          h += '<path d="M ' + pt(botPts[ei]) + ' L ' + pt(botPts[k]) + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linecap="round" opacity="0.85"/>';
        });
      }
    }

    if (state.antislip && state.antislip !== 'none' && frontEdges.length) {
      var padC, padOp;
      if (state.antislip === 'black_pad') { padC = '#1A1A18'; padOp = 0.32; }
      else if (state.antislip === 'beige_pad') { padC = '#C8B898'; padOp = 0.4; }
      else { padC = '#4A4020'; padOp = 0.26; }
      var stripH = 3;
      frontEdges.forEach(function (ei) {
        var k = (ei + 1) % topPts.length;
        h += '<path d="M ' + pt(botPts[ei]) + ' L ' + pt(botPts[k]) + ' L ' + pt([botPts[k][0], botPts[k][1] + stripH]) + ' L ' + pt([botPts[ei][0], botPts[ei][1] + stripH]) + ' Z" fill="' + padC + '" opacity="' + padOp + '"/>';
      });
    }

    function lbl(x, y, t, color, anchor, size) {
      h += '<text x="' + f(x) + '" y="' + f(y) + '" text-anchor="' + (anchor || 'middle') + '" font-size="' + (size || 14) + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + color + '">' + t + '</text>';
    }
    function tag(letter, key) { return state[key] ? letter + ' ' + cmS(state[key]) + ' cm' : letter + ' = ?'; }
    function col(key) { return state[key] ? DIMC : TODO; }
    function arrow(a, b2, color) {
      h += '<line x1="' + f(a[0]) + '" y1="' + f(a[1]) + '" x2="' + f(b2[0]) + '" y2="' + f(b2[1]) + '" stroke="' + color + '" stroke-width="1.1" marker-start="url(#lc-arr)" marker-end="url(#lc-arr)"/>';
    }
    /* edges 0..6 = A (top of long arm), B (upright inner height), C (upright top), D (upright outer height),
       E (right bevel, given by its two legs E1/E2), F (bottom), left bevel (auto) */
    var EDGE = [['A', 'a'], ['B', 'b'], ['C', 'c'], ['D', 'd'], null, ['F', 'f'], null];
    for (i = 0; i < verts.length; i++) {
      if (!EDGE[i]) continue;
      var jj = (i + 1) % verts.length;
      var mxp = (topPts[i][0] + topPts[jj][0]) / 2, myp = (topPts[i][1] + topPts[jj][1]) / 2;
      var ox = 0, oy = 0, horiz = Math.abs(topPts[jj][1] - topPts[i][1]) < 1;
      if (horiz) {   // push above / below (below = past the 3D band)
        var below = frontEdges.indexOf(i) >= 0;
        oy = below ? thickPx + 18 : -9;
      } else {       // vertical edge: push sideways, away from the shape
        var probe = [mxp + 4, myp];
        ox = inPoly(probe) ? -9 : 9;
      }
      lbl(mxp + ox, myp + oy + (horiz ? 0 : 5), tag(EDGE[i][0], EDGE[i][1]), col(EDGE[i][1]), horiz ? 'middle' : (ox > 0 ? 'start' : 'end'));
    }
    /* right bevel legs: E1 across (below the band), E2 down (outside the upright) */
    var v4 = topPts[4], v5 = topPts[5], b5 = botPts[5];
    var e1y = b5[1] + 14;
    h += '<line x1="' + f(v4[0]) + '" y1="' + f(v4[1]) + '" x2="' + f(v4[0]) + '" y2="' + f(e1y + 3) + '" stroke="' + col('e1') + '" stroke-width="0.5" stroke-dasharray="2 2"/>';
    arrow([v5[0], e1y], [v4[0], e1y], col('e1'));
    lbl((v5[0] + v4[0]) / 2, e1y + 17, tag('E1', 'e1'), col('e1'), 'middle', 13);
    var e2x = v4[0] + mir * 14;
    h += '<line x1="' + f(v5[0]) + '" y1="' + f(v5[1]) + '" x2="' + f(e2x + mir * 3) + '" y2="' + f(v5[1]) + '" stroke="' + col('e2') + '" stroke-width="0.5" stroke-dasharray="2 2"/>';
    arrow([e2x, v4[1]], [e2x, v5[1]], col('e2'));
    lbl(e2x + mir * 6, (v4[1] + v5[1]) / 2 + 5, tag('E2', 'e2'), col('e2'), mir > 0 ? 'start' : 'end', 13);
    if (!empty && g.valid) {   // derived left bevel
      var v6 = topPts[6], v0 = topPts[0];
      lbl((v6[0] + v0[0]) / 2 - mir * 12, (v6[1] + v0[1]) / 2 + 4, cmS(Math.sqrt(g.g1 * g.g1 + g.g2 * g.g2)) + ' cm auto', '#B06A2A', mir > 0 ? 'end' : 'start', 11);
    }

    var thkC = T ? '#3d5e3f' : TODO, ls = 13;
    var ti = 0, tbx = 1e9;
    frontEdges.forEach(function (ei) {
      var k = (ei + 1) % topPts.length;
      if (topPts[ei][0] * mir < tbx) { tbx = topPts[ei][0] * mir; ti = ei; }
      if (topPts[k][0] * mir < tbx) { tbx = topPts[k][0] * mir; ti = k; }
    });
    var thkPt = topPts[ti], thkBt = botPts[ti], hug = -16 * mir;
    h += '<line x1="' + f(thkPt[0]) + '" y1="' + f(thkPt[1]) + '" x2="' + f(thkPt[0] + hug) + '" y2="' + f(thkPt[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(thkBt[0]) + '" y1="' + f(thkBt[1]) + '" x2="' + f(thkBt[0] + hug) + '" y2="' + f(thkBt[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(thkPt[0] + hug) + '" y1="' + f(thkPt[1] + 2) + '" x2="' + f(thkBt[0] + hug) + '" y2="' + f(thkBt[1] - 2) + '" stroke="' + thkC + '" stroke-width="1.3" marker-start="url(#lc-arr)" marker-end="url(#lc-arr)"/>';
    h += '<text x="' + f(thkPt[0] + hug - 5 * mir) + '" y="' + f((thkPt[1] + thkBt[1]) / 2 + 4) + '" text-anchor="' + (mir > 0 ? 'end' : 'start') + '" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + thkC + '">' + (T ? 'H ' + cmS(T) + ' cm' : 'H = ?') + '</text>';

    var cap = empty ? 'Miss jede Kante mit Buchstaben · die linke Schräge rechnen wir aus'
      : 'Gesamt ' + cmS(g.overallW) + ' × ' + cmS(g.overallH) + ' cm  ·  Schräge links ' + cmS(g.g1) + ' × ' + cmS(g.g2) + ' cm (auto)';
    h += '<text x="' + f(cx) + '" y="' + f(Math.min(cy + halfHpx + thickPx + 52, 262)) + '" text-anchor="middle" font-size="11" font-family="Poppins,sans-serif" font-weight="600" fill="#8A6A3A">' + cap + '</text>';

    svg.innerHTML = h;
  }

  /* ─── Nook preview — ASSEMBLED L "plan from above" (zotra "Plan von oben" style):
     the corner square + the two straight seats shown IN PLACE as the customer's bench,
     each labelled with its derived size. Zipper = the two back (wall) edges. ─── */
  function drawNook() {
    var svg = els.dimSvg;
    if (!svg) return;
    svg.setAttribute('viewBox', '0 0 440 270');
    if (diamondMode()) { drawNookDiamond(svg); return; }
    function f(n) { return n.toFixed(1); }

    var empty = !(state.sideL || state.sideR || state.depth);
    var sideL = state.sideL || 60, sideR = state.sideR || 84, depth = state.depth || 20, T = state.thickness || 2;
    if (sideL < depth + 4 || sideR < depth + 4) { sideL = Math.max(sideL, depth + 4); sideR = Math.max(sideR, depth + 4); }
    var TODO = '#C0612B';
    function need(key, name, val) { return state[key] ? name + ' ' + cmS(val) + ' cm' : name + ' = ?'; }
    function needC(key, c) { return state[key] ? c : TODO; }
    var seatL = +(sideL - depth).toFixed(2), seatR = +(sideR - depth).toFixed(2);

    var tri = cornerIsTriangle();
    var paint = fabricPaint(empty);
    var topC = paint.topC, strokeC = paint.strokeC;

    /* Plan layout (inches, y grows downward): corner at top-left (0,0)-(d,d),
       right seat = horizontal arm to the right, left seat = vertical arm downward.
       The three cushions are SEPARATE pieces — drawn with a seam gap between them. */
    /* triangle: right angle at the INNER corner (depth,depth) so its two legs mate the
       two seats (right leg → right seat's left edge, bottom leg → left seat's top edge);
       hypotenuse faces the outer wall corner. */
    var cornerPoly = tri ? [[depth, 0], [depth, depth], [0, depth]]
                         : [[0, 0], [depth, 0], [depth, depth], [0, depth]];
    var rightRect = [[depth, 0], [sideR, 0], [sideR, depth], [depth, depth]];
    var leftRect  = [[0, depth], [depth, depth], [depth, sideL], [0, sideL]];

    var gW = sideR, gH = sideL;
    var sc = Math.min(300 / gW, 178 / gH);
    var offX = (440 - gW * sc) / 2 + 8, offY = 36;
    function P(p) { return [offX + p[0] * sc, offY + p[1] * sc]; }
    function s(a) { return a[0].toFixed(1) + ',' + a[1].toFixed(1); }
    /* Shrink a screen-space polygon toward its own centroid so separate pieces show a gap */
    function inset(sv, d) {
      var cx = 0, cy = 0; sv.forEach(function (p) { cx += p[0]; cy += p[1]; }); cx /= sv.length; cy /= sv.length;
      return sv.map(function (p) { var dx = cx - p[0], dy = cy - p[1], L = Math.hypot(dx, dy) || 1; return [p[0] + dx / L * d, p[1] + dy / L * d]; });
    }

    var h = '';
    h += '<defs><marker id="nk-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">'
      + '<path d="M1 1.5L8.5 5L1 8.5" fill="none" stroke="context-stroke" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></marker></defs>';
    function lbl(x, y, t, size, color, anchor) { return '<text x="' + f(x) + '" y="' + f(y) + '" text-anchor="' + (anchor || 'middle') + '" font-size="' + (size || 8.5) + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + (color || '#6a5a4a') + '">' + t + '</text>'; }
    /* Draw one piece from model verts + its back(wall) edges [i,j] to mark as zipper */
    function piece(verts, zipEdges) {
      var sv = inset(verts.map(P), 2.6);
      var out = '<polygon points="' + sv.map(s).join(' ') + '" fill="' + topC + '" stroke="' + strokeC + '" stroke-width="1.6" stroke-linejoin="round"/>';
      (zipEdges || []).forEach(function (e) {
        var A = sv[e[0]], B = sv[e[1]];
        out += '<line x1="' + f(A[0]) + '" y1="' + f(A[1]) + '" x2="' + f(B[0]) + '" y2="' + f(B[1]) + '" stroke="#3A7D2C" stroke-width="2.8" stroke-linecap="round" opacity="0.92"/>';
      });
      return out;
    }

    /* zipper (back/wall) edges per piece: corner = top + left; seats = their wall edge */
    /* zipper (back) edge: triangle = the hypotenuse (faces the wall corner); square = top + left walls */
    var cornerZip = tri ? [[2, 0]] : [[0, 1], [cornerPoly.length - 1, 0]];
    h += piece(rightRect, [[0, 1]]);              // right seat: top wall edge
    h += piece(leftRect,  [[3, 0]]);              // left seat: left wall edge ((0,sideL)->(0,depth))
    h += piece(cornerPoly, cornerZip);
    var zt = P([(depth + sideR) / 2, depth]);
    h += lbl(zt[0], zt[1] + 14, 'grün = Reißverschluss (hinten)', 10, '#3A7D2C');

    /* Piece labels, centered in each piece */
    var cc = P(tri ? [depth * 0.58, depth * 0.58] : [depth / 3.2, depth / 3.2]);
    h += lbl(cc[0], cc[1] + 4, 'Ecke', 10, '#6a5a4a');
    var rc = P([(depth + sideR) / 2, depth / 2]);
    h += lbl(rc[0], rc[1] - (empty ? -4 : 3), 'Sitz rechts', 12, '#6a5a4a');
    if (!empty) h += lbl(rc[0], rc[1] + 11, cmS(seatR) + '×' + cmS(depth) + ' cm', 11, '#8A6A3A');
    var lcp = P([depth / 2, (depth + sideL) / 2]);
    h += lbl(lcp[0], lcp[1] - (empty ? -4 : 3), 'Links', 12, '#6a5a4a');
    if (!empty) h += lbl(lcp[0], lcp[1] + 11, cmS(seatL) + '×' + cmS(depth) + ' cm', 11, '#8A6A3A');

    /* Input-dim callouts: Right side (top), Left side (left), Seat depth */
    var topA = P([0, 0]), topB = P([sideR, 0]);
    var cR = needC('sideR', '#1a4f78'), cL = needC('sideL', '#1a4f78'), cD = needC('depth', '#3d5e3f');
    h += '<line x1="' + f(topA[0]) + '" y1="' + f(offY - 12) + '" x2="' + f(topB[0]) + '" y2="' + f(offY - 12) + '" stroke="' + cR + '" stroke-width="1.1" marker-start="url(#nk-arr)" marker-end="url(#nk-arr)"/>';
    h += lbl((topA[0] + topB[0]) / 2, offY - 18, need('sideR', 'Länge rechts', sideR), 14, cR);
    var lA = P([0, 0]), lB = P([0, sideL]);
    h += '<line x1="' + f(offX - 12) + '" y1="' + f(lA[1]) + '" x2="' + f(offX - 12) + '" y2="' + f(lB[1]) + '" stroke="' + cL + '" stroke-width="1.1" marker-start="url(#nk-arr)" marker-end="url(#nk-arr)"/>';
    h += '<text x="' + f(offX - 18) + '" y="' + f((lA[1] + lB[1]) / 2) + '" text-anchor="middle" font-size="14" font-family="Poppins,sans-serif" font-weight="600" fill="' + cL + '" transform="rotate(-90 ' + f(offX - 18) + ' ' + f((lA[1] + lB[1]) / 2) + ')">' + need('sideL', 'Länge links', sideL) + '</text>';
    var dA = P([sideR, 0]), dB = P([sideR, depth]);
    h += '<line x1="' + f(dA[0] + 12) + '" y1="' + f(dA[1]) + '" x2="' + f(dB[0] + 12) + '" y2="' + f(dB[1]) + '" stroke="' + cD + '" stroke-width="1.1" marker-start="url(#nk-arr)" marker-end="url(#nk-arr)"/>';
    h += lbl(dA[0] + 17, (dA[1] + dB[1]) / 2 + 5, need('depth', 'Sitztiefe', depth), 13, cD, 'start');

    var cornerName = tri ? 'Dreieckige Ecke' : 'Quadratische Ecke';
    h += lbl(220, 264, empty ? 'Miss die Bank an der Wand entlang bis in die Ecke' : cornerName + ' · 3 einzelne Polster · alle ' + cmS(T) + ' cm hoch', 11, '#8A6A3A');

    svg.innerHTML = h;
  }

  /* ─── Diamond-mode nook preview — image-7 style: a real diamond hexagon corner +
     two explicit benches (left horizontal, right vertical) laid out around it. ─── */
  var activeDiamondField = '';
  function drawNookDiamond(svg) {
    function f(n){return Number(n).toFixed(1);}
    var te=state.bw2||18.5, be=state.dbe||7.1, bc=state.dbh||7.1;
    var bw1=state.bw1||18.5, cd=state.dcd||18.9;
    var bl1=state.bl1||41.3, bl2=state.bl2||27.3;
    var gap=1.0, T=state.thickness||4;
    var k=diamondBevel(te,be,bc,bw1,cd);
    var demo=false;
    if(!Number.isFinite(k)||k<=0.1||be>=k+te||bc>=k+bw1){
      // Keep a useful labelled example visible for invalid dimensions.
      te=18.5;be=7.1;bc=7.1;bw1=18.5;cd=18.9;k=diamondBevel(te,be,bc,bw1,cd);demo=true;
    }
    var corner=diamondModel(te,be,bc,bw1,cd), H=k+bw1, rightX=k+te;
    var left=[[-gap-bl1,k],[-gap,k],[-gap,H],[-gap-bl1,H]];
    var right=[[k,-gap-bl2],[rightX,-gap-bl2],[rightX,-gap],[k,-gap]];
    var all=corner.concat(left,right),xs=all.map(function(p){return p[0];}),ys=all.map(function(p){return p[1];});
    var minX=Math.min.apply(null,xs),maxX=Math.max.apply(null,xs),minY=Math.min.apply(null,ys),maxY=Math.max.apply(null,ys);
    var scale=Math.min(370/Math.max(1,maxX-minX),166/Math.max(1,maxY-minY));
    var ox=(440-(maxX-minX)*scale)/2,oy=28+(166-(maxY-minY)*scale)/2;
    function P(p){return [ox+(p[0]-minX)*scale,oy+(p[1]-minY)*scale];}
    function pt(p){var q=P(p);return f(q[0])+','+f(q[1]);}
    var paint=fabricPaint(false),h='';
    function poly(v){return '<polygon points="'+v.map(pt).join(' ')+'" fill="'+paint.topC+'" stroke="'+paint.strokeC+'" stroke-width="1.6" stroke-linejoin="round"/>';}
    function label(p,t,size,color,dy,anchor,dx){var q=P(p);return '<text x="'+f(q[0]+(dx||0))+'" y="'+f(q[1]+(dy||0))+'" text-anchor="'+(anchor||'middle')+'" font-size="'+(size||12)+'" font-weight="600" font-family="Poppins,sans-serif" fill="'+(color||'#665d50')+'">'+t+'</text>';}
    function lenTxt(key){return state[key]?'Länge '+cmS(state[key])+' cm':'Länge = ?';}
    h+=poly(left)+poly(right)+poly(corner);
    h+=label([-gap-bl1/2,k+bw1/2],'Bank links',12,'#665d50',-4);
    h+=label([-gap-bl1/2,k+bw1/2],lenTxt('bl1'),12,state.bl1?'#346B91':'#C0612B',12);
    h+=label([rightX,-gap-bl2/2],'Bank rechts',12,'#665d50',-4,'start',10);
    h+=label([rightX,-gap-bl2/2],lenTxt('bl2'),12,state.bl2?'#346B91':'#C0612B',12,'start',10);
    var eds=[
      {a:0,b:1,id:'AB',key:'bw2',val:te,off:-14},
      {a:1,b:2,id:'BC',key:'dbh',val:bc,off:-20},
      {a:2,b:3,id:'CD',key:'dcd',val:cd,off:-16},
      {a:3,b:4,id:'ED',key:'dbe',val:be,off:-16},
      {a:4,b:5,id:'FE',key:'bw1',val:bw1,off:16},
      {a:5,b:0,id:'AF',key:'af',val:Math.SQRT2*k,off:18}
    ];
    eds.forEach(function(e){
      var a=P(corner[e.a]),b=P(corner[e.b]),dx=b[0]-a[0],dy=b[1]-a[1],L=Math.hypot(dx,dy)||1;
      var nx=-dy/L,ny=dx/L,hi=activeDiamondField===e.key;
      var given=(e.key==='af')||!!state[e.key];
      var color=hi?'#D66532':(given?'#346B91':'#C0612B');
      h+='<line x1="'+f(a[0])+'" y1="'+f(a[1])+'" x2="'+f(b[0])+'" y2="'+f(b[1])+'" stroke="'+color+'" stroke-width="'+(hi?4:2.2)+'" stroke-linecap="round"/>';
      var mx=(a[0]+b[0])/2+nx*e.off,my=(a[1]+b[1])/2+ny*e.off;
      h+='<text x="'+f(mx)+'" y="'+f(my)+'" text-anchor="middle" dominant-baseline="central" font-size="'+(hi?13:11)+'" font-weight="700" font-family="Poppins,sans-serif" fill="'+color+'">'+(e.key==='af'?e.id+' auto':(given?e.id+' '+cmS(e.val)+' cm':e.id+' = ?'))+'</text>';
    });
    var anyD=state.bw2||state.dbe||state.dbh||state.dcd||state.bw1||state.bl1||state.bl2;
    h+='<text x="220" y="258" text-anchor="middle" font-size="11" font-family="Poppins,sans-serif" fill="#8A6A3A">'+(demo&&anyD?'Diese Maße ergeben noch keine Raute · Beispiel wird angezeigt':(!anyD?'Tippe auf ein Feld, um seine Kante hervorzuheben · AF rechnen wir aus':'3 einzelne Polster · '+cmS(T)+' cm hoch · Ansicht von oben'))+'</text>';
    svg.innerHTML=h;
  }

  /* ─── Summary ─── */
  function updateSummary() {
    if (!els.summary) return;
    var parts;
    if (isNook()) {
      parts = ['Eckbank-Set · 3-teilig'];
      if (nookDimsValid()) {
        if (diamondMode()) parts.push('Eckstück ' + cmS(state.dte) + '/' + cmS(state.dmw) + '/' + cmS(state.dbe) + ' cm · Bänke ' + cmS(state.bl1) + '×' + cmS(state.bw1) + ' & ' + cmS(state.bl2) + '×' + cmS(state.bw2) + ' cm · H ' + cmS(state.thickness) + ' cm');
        else parts.push((cornerIsTriangle() ? 'dreieckige Ecke' : 'quadratische Ecke') + ' · links ' + cmS(state.sideL) + ' cm · rechts ' + cmS(state.sideR) + ' cm · Tiefe ' + cmS(state.depth) + ' cm · H ' + cmS(state.thickness) + ' cm');
      }
    } else {
      var g = geom(state);
      parts = ['L-Form · abgeschrägte Ecken'];
      if (g.valid) parts.push(cmS(g.overallW) + ' × ' + cmS(g.overallH) + ' cm · H ' + cmS(state.thickness) + ' cm');
    }
    if (state.fabric) parts.push(state.fabric.title);
    if (state.piping && state.piping !== 'none') {
      var pl = LABELS[state.piping];
      if (state.pipingColor && state.pipingColor !== 'match') pl += ' (' + (LABELS[state.pipingColor] || state.pipingColor) + ')';
      parts.push(pl);
    }
    if (state.straps && state.straps !== 'none') parts.push(LABELS[state.straps]);
    if (state.antislip && state.antislip !== 'none') parts.push(LABELS[state.antislip]);
    els.summary.textContent = parts.join(' · ');
  }

  /* ─── Recalc ─── */
  function gv(id) { var el = document.getElementById('cc-dim-' + id); return el ? parseNum(el.value) / CM_PER_IN : 0; }
  var OK_IDS = ['a', 'b', 'c', 'd', 'e1', 'e2', 'f', 't', 'sideL', 'sideR', 'depth',
                'dte', 'dmw', 'dbe', 'dth', 'dbh', 'dcd', 'bl1', 'bw1', 'bl2', 'bw2'];

  function recalc() {
    var _cf = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (_cf && _cf !== state.fabric) state.fabric = _cf;

    if (diamondMode()) {
      state.dte = gv('bw2'); state.dbe = gv('dbe'); state.dbh = gv('dbh'); state.dcd = gv('dcd'); state.dth = diamondBevel(gv('bw2'),state.dbe,state.dbh,gv('bw1'),state.dcd); state.dmw = state.dth + state.dte;
      state.bl1 = gv('bl1'); state.bw1 = gv('bw1'); state.bl2 = gv('bl2'); state.bw2 = gv('bw2');
    } else if (isNook()) {
      state.sideL = gv('sideL'); state.sideR = gv('sideR'); state.depth = gv('depth');
    } else {
      state.a = gv('a'); state.b = gv('b'); state.c = gv('c'); state.d = gv('d');
      state.e1 = gv('e1'); state.e2 = gv('e2'); state.f = gv('f');
    }
    state.thickness = gv('t');

    state.extraCovers = els.extraCovers ? els.extraCovers.value : '0';
    state.throwPillow = els.throwPillow ? els.throwPillow.value : 'none';
    state.pillowCovers = els.pillowCovers ? els.pillowCovers.value : 'none';
    state.armrestPillow = els.armrestPillow ? els.armrestPillow.value : 'none';
    state.zipperPlacement = els.zipperPlacement ? els.zipperPlacement.value : '';
    state.pipingColor = els.pipingColor ? els.pipingColor.value : 'match';
    state.notes = els.notes ? els.notes.value : '';

    if (els.pipingColorWrap) {
      var show = (state.piping === 'single' || state.piping === 'double');
      els.pipingColorWrap.style.display = show ? '' : 'none';
      if (!show) { state.pipingColor = 'match'; if (els.pipingColor) els.pipingColor.value = 'match'; }
    }

    OK_IDS.forEach(function (id) {
      var el = document.getElementById('cc-dim-' + id);
      if (el) el.classList.toggle('ok', parseNum(el.value) > 0);
    });

    drawCushion();
    updateSummary();

    var geomNote = document.getElementById('cc-lc-geomnote');
    if (geomNote) {
      if (diamondMode()) {
        var anyDia = state.dbe || state.dth || state.dbh || state.bl1 || state.bl2;
        if (!nookDimsValid() && !anyDia) { geomNote.textContent = ''; geomNote.style.color = '#8a7a6a'; }
        else if (!nookDimsValid()) {
          var whyd = '⚠ ED, BC, CD und beide Bankmaße eingeben. Die Schräge AF wird automatisch berechnet.';
          if (state.dcd && !diamondCornerValid()) whyd = '⚠ Mit diesen Maßen ergibt sich keine 45°-Schräge AF. Bitte CD, BC, ED und beide Banktiefen prüfen.';
          geomNote.textContent = whyd; geomNote.style.color = (anyDia ? '#b23b3b' : '#8a7a6a');
        }
        else { geomNote.textContent = 'Wir fertigen: Eckstück (AB ' + cmS(state.dte) + ' · FE ' + cmS(state.bw1) + ' · ED ' + cmS(state.dbe) + ' · BC ' + cmS(state.dbh) + ' · CD ' + cmS(state.dcd) + ' · AF ' + cmS(Math.SQRT2*state.dth) + ' cm) · Bank links ' + cmS(state.bl1) + '×' + cmS(state.bw1) + ' cm · Bank rechts ' + cmS(state.bl2) + '×' + cmS(state.bw2) + ' cm'; geomNote.style.color = '#3A7D2C'; }
      } else if (isNook()) {
        var anyNook = state.sideL || state.sideR || state.depth;
        if (!nookDimsValid() && !anyNook) { geomNote.textContent = ''; geomNote.style.color = '#8a7a6a'; }
        else if (!nookDimsValid()) {
          var why = '⚠ Länge links, Länge rechts und Sitztiefe eingeben';
          if (state.depth && state.depth < 8) why = '⚠ Die Sitztiefe wirkt klein — die meisten Bänke sind 35–60 cm tief';
          else if (state.depth && (state.sideL && state.sideL < state.depth + 4)) why = '⚠ Die linke Seite muss länger als die Sitztiefe sein (sie enthält die Ecke)';
          else if (state.depth && (state.sideR && state.sideR < state.depth + 4)) why = '⚠ Die rechte Seite muss länger als die Sitztiefe sein (sie enthält die Ecke)';
          geomNote.textContent = why; geomNote.style.color = (anyNook ? '#b23b3b' : '#8a7a6a');
        }
        else { geomNote.textContent = 'Wir fertigen: Ecke ' + cmS(state.depth) + '×' + cmS(state.depth) + ' cm · Sitz links ' + cmS(derivedSeatL()) + '×' + cmS(state.depth) + ' cm · Sitz rechts ' + cmS(derivedSeatR()) + '×' + cmS(state.depth) + ' cm'; geomNote.style.color = '#3A7D2C'; }
      } else {
        var g = geom(state);
        if (!g.valid && !(state.a || state.b || state.c)) geomNote.textContent = '';
        else if (!g.valid) geomNote.textContent = '⚠ ' + g.errs[0];
        else geomNote.textContent = 'Schräge links (auto): ' + cmS(g.g1) + ' × ' + cmS(g.g2) + ' cm  ·  langer Schenkel ' + cmS(g.longArmW) + ' × ' + cmS(g.longArmD) + ' cm  ·  aufrechter Teil ' + cmS(g.uprightW) + ' × ' + cmS(g.uprightH) + ' cm';
        geomNote.style.color = (!g.valid && (state.a || state.b || state.c)) ? '#b23b3b' : '#8a7a6a';
      }
    }

    var dimsOk = hasDims();
    var ec = parseInt(state.extraCovers) || 0;
    updateExtraCoverLabels();
    var price = dimsOk ? calcPrice() : null;
    var addonTotal = getAddonTotal();

    if (price !== null) {
      els.priceMain.textContent = Math.round(price + addonTotal) + ' €';
      var parts = ['Grundpreis ' + Math.round(price) + ' €'];
      if (ADDON.piping[state.piping])     parts.push('Keder +' + ADDON.piping[state.piping] + ' €');
      if (ADDON.pipingColor[state.pipingColor]) parts.push('Keder-Farbe +' + ADDON.pipingColor[state.pipingColor] + ' €');
      if (ADDON.straps[state.straps])     parts.push('Bänder +' + ADDON.straps[state.straps] + ' €');
      if (ADDON.zipper[state.zipper])     parts.push('Reißverschluss +' + ADDON.zipper[state.zipper] + ' €');
      if (ADDON.antislip[state.antislip]) parts.push('Anti-Rutsch +' + ADDON.antislip[state.antislip] + ' €');
      if (ec > 0)                         parts.push('Zusätzliche Bezüge +' + (extraCoverUnit() * ec) + ' €');
      if (ADDON.throwPillow[state.throwPillow])   parts.push('Dekokissen +' + ADDON.throwPillow[state.throwPillow] + ' €');
      if (ADDON.pillowCovers[state.pillowCovers]) parts.push('Kissenbezüge +' + ADDON.pillowCovers[state.pillowCovers] + ' €');
      if (ADDON.armrestPillow[state.armrestPillow]) parts.push('Armlehnenkissen +' + ADDON.armrestPillow[state.armrestPillow] + ' €');
      els.priceBreak.textContent = parts.join(' · ');
    } else {
      els.priceMain.textContent = '— €';
      els.priceBreak.textContent = isNook()
        ? (diamondMode() ? 'Eckstück und beide Bankmaße eingeben, um den Preis zu sehen' : 'Links, rechts und Sitztiefe eingeben, um den Preis zu sehen')
        : 'Alle 7 Maße und die Höhe eingeben, um den Preis zu sehen';
    }

    var complete = isComplete();
    [els.addBtn, els.addBtnSticky].filter(Boolean).forEach(function (btn) {
      btn.disabled = !complete;
      btn.textContent = (complete && price !== null) ? 'Zur Bestellung hinzufügen — ' + Math.round(price + addonTotal) + ' € →' : 'Zur Bestellung hinzufügen →';
    });

    if (els.addNote) {
      if (complete) els.addNote.textContent = 'Kostenloser Versand · Versand in ca. 20 Werktagen';
      else {
        var missing = [];
        if (!dimsOk) missing.push('alle Maße');
        if (!state.fabric) missing.push('Stoff');
        if (!state.piping) missing.push('Keder');
        if (!state.straps) missing.push('Bänder');
        if (!state.antislip) missing.push('Anti-Rutsch');
        els.addNote.textContent = missing.length ? missing.join(', ') + ' auswählen, um fortzufahren' : '';
      }
    }

    document.dispatchEvent(new CustomEvent('configurator:price-updated', {
      detail: { price: price !== null ? Math.round(price + addonTotal) : null, state: state }
    }));
  }

  /* ─── Card selection ─── */
  function pickCard(el, group, value) {
    if (group === 'cornerSetShape') {
      /* One Shape-section choice controls both the family and the corner type. */
      state.shape = 'nook';
      state.cornerShape = value;
      refreshShapeCards();
      buildDimsGrid();
      recalc();
      return;
    }
    el.parentElement.querySelectorAll('.cc-opt-card').forEach(function (c) { c.classList.remove('sel'); });
    el.classList.add('sel');
    state[group] = value;
    if (group === 'shape' || group === 'cornerShape') {
      refreshShapeCards();
      buildDimsGrid();
    }
    recalc();
  }
  function refreshShapeCards() {
    var grid=document.getElementById('cc-shape-grid');
    if(!grid)return;
    grid.querySelectorAll('.cc-opt-card[data-group="shape"],.cc-opt-card[data-group="cornerSetShape"]').forEach(function(card){
      var selected=card.dataset.group==='shape' ? state.shape===card.dataset.value
        : state.shape==='nook' && state.cornerShape===card.dataset.value;
      card.classList.toggle('sel',selected);
      card.setAttribute('aria-pressed',selected?'true':'false');
    });
  }

  /* ─── Line properties for cart ─── */
  function addCommonProps(props) {
    var ec = parseInt(state.extraCovers) || 0;
    if (state.fabric) {
      props['Stoff'] = state.fabric.title || '';
      props['_Fabric SKU'] = (state.fabric.variants && state.fabric.variants[0] && state.fabric.variants[0].sku) || '';
      props['_Fabric Handle'] = state.fabric.handle || '';
      props['_Fabric Image'] = (state.fabric.images && state.fabric.images[0] && state.fabric.images[0].src) || '';
      var _grade = extractTag(state.fabric.tags || [], 'grade');
      if (_grade) props['Stoffqualität'] = _grade.charAt(0).toUpperCase() + _grade.slice(1);
    }
    if (ec > 0) props['Zusätzliche Bezüge'] = ec + (ec === 1 ? ' Set' : ' Sets');
    if (state.throwPillow !== 'none') props['Dekokissen'] = pillowLabel(state.throwPillow);
    if (state.pillowCovers !== 'none') props['Kissenbezüge'] = pillowLabel(state.pillowCovers);
    if (state.armrestPillow !== 'none') props['Armlehnenkissen'] = ARMREST_LABEL[state.armrestPillow] || state.armrestPillow;
    if (state.zipperPlacement) props['Reißverschluss-Position'] = state.zipperPlacement;
    if (state.notes.trim()) props['Anmerkung'] = state.notes.trim();
    props['_Source Product'] = (document.getElementById('cc-configurator') || { dataset: {} }).dataset.productTitle || document.title.split('–')[0].split('—')[0].trim() || location.pathname;
  }

  function getLineProperties() {
    var ec = parseInt(state.extraCovers) || 0;
    var price = calcPrice();
    var finalPrice = Math.round(price + getAddonTotal());

    var props = {};
    var apiPayload;

    if (diamondMode()) {
      var T = state.thickness;
      props['Form'] = 'Eckbank-Set (3-teilig, Eckstück Raute)';
      props['Eckstück — Oberkante (AB)'] = cmS(state.dte) + ' cm';
      props['Eckstück — max. Breite (berechnet)'] = cmS(state.dmw) + ' cm';
      props['Eckstück — Unterkante (ED)'] = cmS(state.dbe) + ' cm';
      props['Eckstück — AF (berechnet)'] = cmS(Math.SQRT2*state.dth) + ' cm';
      props['Eckstück — CD'] = cmS(state.dcd) + ' cm';
      props['Eckstück — rechte Kante (BC)'] = cmS(state.dbh) + ' cm';
      props['Bank links'] = cmS(state.bl1) + ' × ' + cmS(state.bw1) + ' cm';
      props['Bank rechts'] = cmS(state.bl2) + ' × ' + cmS(state.bw2) + ' cm';
      props['Höhe'] = cmS(T) + ' cm';
      props['_Maße (inch)'] = 'TE ' + round1(state.dte) + ' MW ' + round1(state.dmw) + ' BE ' + round1(state.dbe) + ' BH ' + round1(state.dbh) + ' CD ' + round1(state.dcd) + ' | L ' + round1(state.bl1) + '×' + round1(state.bw1) + ' | R ' + round1(state.bl2) + '×' + round1(state.bw2) + ' | T ' + round1(T) + '"';
      props['Keder'] = LABELS[state.piping] || state.piping;
      props['Keder-Farbe'] = (state.pipingColor && state.pipingColor !== 'match') ? (LABELS[state.pipingColor] || state.pipingColor) : 'Passend zum Stoff';
      props['Bänder'] = LABELS[state.straps] || state.straps;
      props['Reißverschluss'] = LABELS[state.zipper] || state.zipper;
      props['Anti-Rutsch'] = LABELS[state.antislip] || state.antislip;
      addCommonProps(props);
      apiPayload = {
        shape: state.shape, cornerShape: 'diamond',
        dims: { te: state.dte, mw: state.dmw, be: state.dbe, th: state.dth, bh: state.dbh, cd: state.dcd, af: +(Math.SQRT2*state.dth).toFixed(2), bl1: state.bl1, bw1: state.bw1, bl2: state.bl2, bw2: state.bw2, thickness: T },
        pieces: [
          { role: 'corner', shape: 'diamond', te: state.dte, mw: state.dmw, be: state.dbe, th: state.dth, bh: state.dbh, cd: state.dcd, af: +(Math.SQRT2*state.dth).toFixed(2), thickness: T },
          { role: 'left_bench', L: state.bl1, W: state.bw1, thickness: T },
          { role: 'right_bench', L: state.bl2, W: state.bw2, thickness: T }
        ],
        piping: state.piping, pipingColor: state.pipingColor, straps: state.straps,
        zipper: state.zipper, antislip: state.antislip,
        extraCovers: ec, throwPillow: state.throwPillow, pillowCovers: state.pillowCovers, armrestPillow: state.armrestPillow
      };
    } else if (isNook()) {
      var seatL = derivedSeatL(), seatR = derivedSeatR(), d = state.depth;
      var cShapeName = cornerIsTriangle() ? 'triangle' : 'square';
      props['Form'] = 'Eckbank-Set (3-teilig, ' + (cornerIsTriangle() ? 'dreieckige' : 'quadratische') + ' Ecke)';
      /* What the customer measured (the bench) */
      props['Länge links'] = cmS(state.sideL) + ' cm';
      props['Länge rechts'] = cmS(state.sideR) + ' cm';
      props['Sitztiefe'] = cmS(d) + ' cm';
      props['Höhe'] = cmS(state.thickness) + ' cm';
      /* What we'll sew (derived, for the factory) */
      props['_Corner cut'] = cornerIsTriangle()
        ? ('Right triangle, legs ' + round1(d) + '×' + round1(d) + '" (' + cmS(d) + '×' + cmS(d) + ' cm, hypotenuse to the room)')
        : ('Square ' + round1(d) + '×' + round1(d) + '" (' + cmS(d) + '×' + cmS(d) + ' cm)');
      props['_Left seat cut'] = round1(seatL) + '" × ' + round1(d) + '" (' + cmS(seatL) + '×' + cmS(d) + ' cm)';
      props['_Right seat cut'] = round1(seatR) + '" × ' + round1(d) + '" (' + cmS(seatR) + '×' + cmS(d) + ' cm)';
      props['_Maße (inch)'] = 'sideL ' + round1(state.sideL) + ' sideR ' + round1(state.sideR) + ' depth ' + round1(d) + ' T ' + round1(state.thickness) + '"';
      props['Keder'] = LABELS[state.piping] || state.piping;
      props['Keder-Farbe'] = (state.pipingColor && state.pipingColor !== 'match') ? (LABELS[state.pipingColor] || state.pipingColor) : 'Passend zum Stoff';
      props['Bänder'] = LABELS[state.straps] || state.straps;
      props['Reißverschluss'] = LABELS[state.zipper] || state.zipper;
      props['Anti-Rutsch'] = LABELS[state.antislip] || state.antislip;
      addCommonProps(props);
      var cornerPiece = cornerIsTriangle()
        ? { role: 'corner', shape: 'triangle', legA: d, legB: d, depth: d, thickness: state.thickness }
        : { role: 'corner', shape: 'square', depth: d, thickness: state.thickness };
      apiPayload = {
        shape: state.shape, cornerShape: state.cornerShape,
        dims: { sideL: state.sideL, sideR: state.sideR, depth: d, thickness: state.thickness },
        pieces: [
          cornerPiece,
          { role: 'left_seat', L: seatL, W: d, thickness: state.thickness },
          { role: 'right_seat', L: seatR, W: d, thickness: state.thickness }
        ],
        piping: state.piping, pipingColor: state.pipingColor, straps: state.straps,
        zipper: state.zipper, antislip: state.antislip,
        extraCovers: ec, throwPillow: state.throwPillow, pillowCovers: state.pillowCovers, armrestPillow: state.armrestPillow
      };
    } else {
      var g = geom(state);
      props['Form'] = 'L-Form mit abgeschrägten Ecken';
      props['A · Oberkante'] = cmS(state.a) + ' cm';
      props['B · Innenhöhe aufrechter Teil'] = cmS(state.b) + ' cm';
      props['C · obere Breite aufrechter Teil'] = cmS(state.c) + ' cm';
      props['D · Höhe rechts'] = cmS(state.d) + ' cm';
      props['Schräge rechts (E1×E2)'] = cmS(state.e1) + ' × ' + cmS(state.e2) + ' cm';
      props['F · Unterkante'] = cmS(state.f) + ' cm';
      props['Schräge links (auto)'] = cmS(g.g1) + ' × ' + cmS(g.g2) + ' cm';
      props['Gesamtmaß'] = cmS(g.overallW) + ' × ' + cmS(g.overallH) + ' cm';
      props['Höhe'] = cmS(state.thickness) + ' cm';
      props['_Maße (inch)'] = 'A ' + round1(state.a) + ' B ' + round1(state.b) + ' C ' + round1(state.c) + ' D ' + round1(state.d) + ' E1 ' + round1(state.e1) + ' E2 ' + round1(state.e2) + ' F ' + round1(state.f) + ' T ' + round1(state.thickness) + '"';
      props['Keder'] = LABELS[state.piping] || state.piping;
      props['Keder-Farbe'] = (state.pipingColor && state.pipingColor !== 'match') ? (LABELS[state.pipingColor] || state.pipingColor) : 'Passend zum Stoff';
      props['Bänder'] = LABELS[state.straps] || state.straps;
      props['Reißverschluss'] = LABELS[state.zipper] || state.zipper;
      props['Anti-Rutsch'] = LABELS[state.antislip] || state.antislip;
      addCommonProps(props);
      apiPayload = {
        shape: 'lcut',
        orient: state.orient,
        dims: { a: state.a, b: state.b, c: state.c, d: state.d, e1: state.e1, e2: state.e2, f: state.f, thickness: state.thickness },
        pieces: [
          { role: 'longarm', L: +g.longArmW.toFixed(2), W: +g.longArmD.toFixed(2), thickness: state.thickness },
          { role: 'upright', L: +g.uprightW.toFixed(2), W: +g.uprightH.toFixed(2), thickness: state.thickness }
        ],
        piping: state.piping, pipingColor: state.pipingColor, straps: state.straps,
        zipper: state.zipper, antislip: state.antislip,
        extraCovers: ec, throwPillow: state.throwPillow, pillowCovers: state.pillowCovers, armrestPillow: state.armrestPillow
      };
    }

    return { properties: props, price: finalPrice, apiPayload: apiPayload };
  }

  /* ─── Build the dims grid for the current shape ─── */
  function buildDimsGrid() {
    var dimsGrid = document.querySelector('.cc-sp-dims:not(.cc-back-dims)');
    if (!dimsGrid) return;
    dimsGrid.style.gridTemplateColumns = 'repeat(4, 1fr)';

    function fld(id, label, ph, mn, mx) {
      return '<div class="cc-field">'
        + '<label class="cc-fl" for="cc-dim-' + id + '">' + label + ' <span class="cc-req">*</span></label>'
        + '<input class="cc-fi" id="cc-dim-' + id + '" type="number" min="' + mn + '" max="' + mx + '" placeholder="' + ph + '" inputmode="decimal"></div>';
    }
    function sub(t) {
      return '<div style="grid-column:1/-1;font-size:11px;font-weight:600;color:#6a5a4a;padding:5px 2px 0;font-family:Poppins,sans-serif">' + t + '</div>';
    }
    var note ='<div id="cc-lc-geomnote" style="grid-column:1/-1;font-size:11px;font-family:Poppins,sans-serif;color:#8a7a6a;padding:2px 2px 0;line-height:1.4"></div>';

    if (isNook()) {
      /* Corner shape selection now lives in the main Shape section. */
      if (cornerIsDiamond()) {
        /* diamond = real diamond corner (its own 5 dims) + two explicit benches */
        var hintD='<div style="grid-column:1/-1;font-size:11px;color:#8a7a6a;font-family:Poppins,sans-serif">Tippe auf ein Feld, um die Kante oben hervorzuheben. AB und FE entsprechen den Banktiefen; AF wird mit einer 45°-Schräge berechnet. Miss immer deine tatsächliche Sitzfläche.</div>';
        dimsGrid.innerHTML=hintD+sub('Eckstück (Raute)')
          +fld('dbe','ED · Unterkante (cm)','z. B. 18',5,560)
          +fld('dbh','BC · kurze Kante rechts (cm)','z. B. 18',5,405)
          +fld('dcd','CD · lange Diagonale (cm)','z. B. 48',5,610)
          +sub('Bank links')
          +fld('bl1','Länge (cm)','z. B. 105',15,560)
          +fld('bw1','FE · Tiefe (cm)','z. B. 47',5,355)
          +sub('Bank rechts')
          +fld('bl2','Länge (cm)','z. B. 69',15,560)
          +fld('bw2','AB · Tiefe (cm)','z. B. 47',5,355)
          +sub('Alle Teile')
          +fld('t','Höhe (cm)','z. B. 10',3,20)
          +note;
      } else {
        var hint = '<div style="grid-column:1/-1;font-size:11px;color:#8a7a6a;padding:3px 2px 2px;font-family:Poppins,sans-serif">Miss die <b>Bank</b>, nicht die Kissen — die drei Teile rechnen wir für dich aus. Für eine rechtwinklige Ecke (90°); alle Maße in cm.</div>';
        dimsGrid.innerHTML =
            hint
          + sub('Miss deine Bank (an der Rückwand entlang, bis in die Ecke)')
          + fld('sideL', 'Länge links (cm)', 'z. B. 150', 30, 405)
          + fld('sideR', 'Länge rechts (cm)', 'z. B. 210', 30, 405)
          + fld('depth', 'Sitztiefe (cm)', 'z. B. 50', 20, 100)
          + fld('t', 'Höhe (cm)', 'z. B. 5', 3, 20)
          + note;
      }
    } else {
      dimsGrid.innerHTML =
          fld('a', 'A · Oberkante (cm)', 'z. B. 236', 15, 405)
        + fld('b', 'B · Innenhöhe aufrecht (cm)', 'z. B. 38', 8, 305)
        + fld('c', 'C · obere Breite aufrecht (cm)', 'z. B. 48', 8, 200)
        + fld('d', 'D · Höhe rechts (cm)', 'z. B. 76', 8, 305)
        + fld('e1', 'E1 · Schräge rechts quer (cm)', 'z. B. 57', 0, 200)
        + fld('e2', 'E2 · Schräge rechts runter (cm)', 'z. B. 48', 0, 200)
        + fld('f', 'F · Unterkante (cm)', 'z. B. 184', 15, 405)
        + fld('t', 'H · Höhe (cm)', 'z. B. 7,5', 3, 20)
        + note;
    }
  }

  /* ─── DOM transformation (adapt shared Liquid section) ─── */
  function transformDOM() {
    var backDims = document.querySelectorAll('.cc-back-dims');
    for (var i = 0; i < backDims.length; i++) backDims[i].style.display = 'none';
    var seatLabel = document.querySelector('.cc-seat-label');
    if (seatLabel) seatLabel.style.display = 'none';

    /* Shape grid → shape FAMILY (Angled L / Nook set) */
    var shapeGrid = document.getElementById('cc-shape-grid');
    if (shapeGrid) {
      function scard(val, name, mini, img) {
        /* img (if given) overlays the mini SVG; on load error it hides and the SVG shows through */
        var overlay = img ? '<img src="' + img + '" alt="" loading="lazy" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover" onerror="this.style.display=\'none\'">' : '';
        return '<div class="cc-opt-card' + (state.shape === val ? ' sel' : '') + '" data-group="shape" data-value="' + val + '">'
          + '<div class="cc-opt-img" style="background:#F5F1E8;position:relative;overflow:hidden">' + mini + overlay + '</div>'
          + '<div class="cc-opt-name">' + name + '</div><div class="cc-opt-price">inklusive</div></div>';
      }
      function cornerShapeCard(val, name, desc, mini, img) {
        var overlay=img ? '<img src="'+img+'" alt="'+name+' Eckpolster" loading="lazy" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover" onerror="this.style.display=\'none\'">' : '';
        var selected=state.shape==='nook' && state.cornerShape===val;
        return '<div class="cc-opt-card'+(selected?' sel':'')+'" data-group="cornerSetShape" data-value="'+val+'" role="button" aria-pressed="'+(selected?'true':'false')+'">'
          +'<div class="cc-opt-img" style="background:#F5F1E8;position:relative;overflow:hidden">'+mini+overlay+'</div>'
          +'<div class="cc-opt-name">'+name+'</div><div class="cc-opt-price">'+desc+'</div></div>';
      }
      shapeGrid.innerHTML = scard('lcut', 'L-Form abgeschrägt', miniLcut('right'), NOOK_IMG.lcut)
        + cornerShapeCard('square', 'Quadratisch', '3-teilig · quadratische Ecke', miniCorner('square'), NOOK_IMG.square)
        + cornerShapeCard('triangle', 'Dreieckig', '3-teilig · dreieckige Ecke', miniCorner('triangle'), NOOK_IMG.triangle)
        + cornerShapeCard('diamond', 'Raute', '3-teilig · Eckstück Raute', miniCorner('diamond'), NOOK_IMG.diamond);
    }
    var shapeHdr = document.querySelector('label[for="cc-shape-grid"], .cc-shape-label');
    if (shapeHdr) shapeHdr.textContent = 'Form';

    /* Move measurements out of the preview panel into their own section,
       immediately after the Shape options. Keep the original grid element
       (and its IDs) so pricing, validation and order properties still work. */
    var inputGrid = document.querySelector('.cc-sp-dims:not(.cc-back-dims)');
    if (inputGrid && shapeGrid) {
      var inputSection = document.getElementById('cc-standalone-measurements');
      if (!inputSection) {
        inputSection = document.createElement('section');
        inputSection.id = 'cc-standalone-measurements';
        inputSection.className = 'cc-standalone-measurements';
        inputSection.innerHTML = '<div class="cc-measurements-title">Maße <span style="color:#b56a45">*</span></div>';
      }
      inputSection.appendChild(inputGrid);
      /* The shape grid's parent is the existing Shape field container. */
      var shapeSection = shapeGrid.parentElement;
      if (shapeSection && shapeSection.parentElement) {
        shapeSection.insertAdjacentElement('afterend', inputSection);
      } else {
        shapeGrid.insertAdjacentElement('afterend', inputSection);
      }
      if (!document.getElementById('cc-standalone-measurements-style')) {
        var css = document.createElement('style');
        css.id = 'cc-standalone-measurements-style';
        css.textContent = [
          '#cc-standalone-measurements{box-sizing:border-box;width:100%;margin:18px 0 24px;padding:18px;border:1px solid #dedbd4;background:#fff;border-radius:6px;clear:both}',
          '#cc-standalone-measurements .cc-measurements-title{font-family:Poppins,sans-serif;font-size:14px;font-weight:600;color:#3b3833;margin-bottom:14px}',
          '#cc-standalone-measurements .cc-sp-dims{display:grid!important;grid-template-columns:repeat(4,minmax(0,1fr))!important;gap:12px!important;width:100%;box-sizing:border-box}',
          '#cc-standalone-measurements .cc-field{min-width:0}',
          '#cc-standalone-measurements .cc-fi{width:100%;box-sizing:border-box}',
          '@media(max-width:767px){#cc-standalone-measurements{padding:14px 12px;margin:14px 0 20px}#cc-standalone-measurements .cc-sp-dims{grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:12px 10px!important}#cc-standalone-measurements .cc-fl{font-size:11px;line-height:1.4;display:block}#cc-standalone-measurements .cc-fi{min-width:0}}',
          '@media(max-width:360px){#cc-standalone-measurements .cc-sp-dims{grid-template-columns:minmax(0,1fr)!important}}'
        ].join('');
        document.head.appendChild(css);
      }
    }

    buildDimsGrid();

    /* Compact mobile preview: remove the redundant helper text and the
       live selection summary below the diagram. Measurements remain in
       their standalone section and all order calculations are untouched. */
    var previewSummary = document.getElementById('cc-sp-summary');
    if (previewSummary) previewSummary.style.display = 'none';
    var previewRoot = document.getElementById('cc-configurator');
    if (previewRoot) {
      var helperNodes = previewRoot.querySelectorAll('p,small,span,div');
      for (var hi = 0; hi < helperNodes.length; hi++) {
        var helper = helperNodes[hi];
        if (helper.children.length) continue;
        if (/^Miss dein vorhandenes Polster oder den Platz, in den es passen soll\.?$/i.test((helper.textContent || '').trim())) {
          helper.style.display = 'none';
        }
      }
    }

    var svg = document.getElementById('cc-dim-svg');
    if (svg) { svg.setAttribute('viewBox', '0 0 440 270'); svg.style.maxHeight = '260px'; svg.style.width = '100%'; }
  }

  /* ─── Init ─── */
  function init() {
    var wrap = document.getElementById('cc-configurator');
    if (!wrap) return;
    var ft = (wrap.getAttribute('data-formula-type') || '').replace('_cover', '');
    if (ft && ft !== 'lcut') return;

    /* Optional per-product default shape via metafield data-default-shape */
    var ds = (wrap.getAttribute('data-default-shape') || '').trim();
    if (ds === 'nook' || ds === 'lcut') state.shape = ds;

    transformDOM();

    els.priceMain    = document.getElementById('cc-price-main');
    els.priceBreak   = document.getElementById('cc-price-break');
    els.summary      = document.getElementById('cc-sp-summary');
    els.dimsGrid     = document.querySelector('.cc-sp-dims:not(.cc-back-dims)');
    els.extraCovers  = document.getElementById('cc-extra-covers');
    els.throwPillow  = document.getElementById('cc-throw-pillow');
    els.pillowCovers = document.getElementById('cc-pillow-covers');
    els.armrestPillow = document.getElementById('cc-armrest-pillow');
    els.zipperPlacement = document.getElementById('cc-zipper-placement');
    els.pipingColor    = document.getElementById('cc-piping-color');
    els.pipingColorWrap = document.getElementById('cc-piping-color-wrap');
    els.notes        = document.getElementById('cc-notes');
    els.addBtn       = document.getElementById('cc-add-btn');
    els.addBtnSticky = document.getElementById('cc-add-btn-sticky');
    els.addNote      = document.getElementById('cc-add-note');
    els.dimSvg       = document.getElementById('cc-dim-svg');

    wrap.addEventListener('click', function (e) {
      var card = e.target.closest('.cc-opt-card');
      if (!card || !card.dataset.group) return;
      pickCard(card, card.dataset.group, card.dataset.value);
    });

    if(els.dimsGrid){
      els.dimsGrid.addEventListener('focusin',function(e){
        if(e.target&&e.target.id&&e.target.id.indexOf('cc-dim-')===0){activeDiamondField=e.target.id.slice(7);if(diamondMode())drawCushion();}
      });
      els.dimsGrid.addEventListener('click',function(e){
      });
    }
    /* Delegated input listener — survives dims-grid innerHTML rebuilds on shape switch */
    if (els.dimsGrid) els.dimsGrid.addEventListener('input', function (e) {
      var t = e.target;
      if (t && t.id && t.id.indexOf('cc-dim-') === 0) recalc();
    });

    if (els.extraCovers) els.extraCovers.addEventListener('change', recalc);
    if (els.throwPillow) els.throwPillow.addEventListener('change', recalc);
    if (els.pillowCovers) els.pillowCovers.addEventListener('change', recalc);
    if (els.armrestPillow) els.armrestPillow.addEventListener('change', recalc);
    if (els.zipperPlacement) els.zipperPlacement.addEventListener('change', recalc);
    if (els.pipingColor) els.pipingColor.addEventListener('change', recalc);
    if (els.notes) els.notes.addEventListener('input', recalc);

    document.addEventListener('fabric:selected', function (e) { state.fabric = e.detail; recalc(); });
    var existingFabric = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (!state.fabric && existingFabric) { state.fabric = existingFabric; recalc(); }
    setInterval(function () {
      var fb = window.__ucfs_selectedFabric || window._ucfsSelected;
      if (fb && fb !== state.fabric) { state.fabric = fb; recalc(); }
    }, 800);

    function handleAdd() {
      if (!isComplete()) return;
      document.dispatchEvent(new CustomEvent('configurator:add-to-cart', { detail: getLineProperties() }));
    }
    if (els.addBtn) els.addBtn.addEventListener('click', handleAdd);
    if (els.addBtnSticky) els.addBtnSticky.addEventListener('click', handleAdd);

    var defaultZipper = wrap.querySelector('.cc-opt-card[data-group="zipper"][data-value="regular"]');
    if (defaultZipper) defaultZipper.classList.add('sel');

    drawCushion();
    recalc();
  }

  window.LcutCushionConfigurator = {
    getState: function () { return state; },
    getLineProperties: getLineProperties,
    recalc: recalc,
    geom: function () { return geom(state); },
    setShape: function (s) { if (s === 'nook' || s === 'lcut') { state.shape = s; refreshShapeCards(); buildDimsGrid(); recalc(); } },
    setCornerShape: function (c) { if (c === 'square' || c === 'triangle' || c === 'diamond') { state.cornerShape = c; state.shape = 'nook'; refreshShapeCards(); buildDimsGrid(); recalc(); } }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
